import { CheckCircle, ChevronDown, ChevronUp, Clock } from "@untitledui/icons";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { OrganizationOnboardingProgressDto } from "@fp/admin-api-client";
import axios from "@/lib/axios";
import { getGetSettingsQueryKey } from "@fp/admin-api-client/settings/settings.client";
import { Button } from "@fp/ui/components/base/buttons/button";

export const allMilestones: Milestone[] = [
	{ key: "BusinessVerified", label: "Verify Your Business", description: "One-time verification that usually takes 24\u201348 hours, but could be sooner.", cta: "Get Started", verb: "Start", route: "/funding" },
	{ key: "AccountFunded", label: "Fund Your Account", description: "Add money to your FieldPay account so you can pay your team instantly.", cta: "Fund Your Account", verb: "Fund", route: "/funding" },
	{ key: "FirstOppCreated", label: "Create Your First Opp", description: "Set up a job opportunity with pay details and requirements.", cta: "Create an Opp", verb: "Create", route: "/opps" },
	{ key: "TalentInvited", label: "Invite Talent Members to Your Roster", description: "Add team members so you can share opportunities and pay them.", cta: "Invite Talent Members", verb: "Invite", route: "/roster" },
	{ key: "FirstPayoutCompleted", label: "Pay Them Out Instantly", description: "Approve a payout request and send money to your team.", cta: "Make a Payout", verb: "Pay", route: "/payout-requests" },
];

const fundingMilestoneKeys = ["BusinessVerified", "AccountFunded"];

export const nonFundingMilestones: Milestone[] = allMilestones.filter((m) => !fundingMilestoneKeys.includes(m.key));

export interface Milestone {
	key: string;
	label: string;
	description: string;
	cta: string;
	verb: string;
	route?: string;
	onClick?: () => void;
	isLoading?: boolean;
}

const pendingMappings: Record<string, string[]> = {
	BusinessVerified: ["BusinessVerificationSubmitted"],
	AccountFunded: ["AccountFundingInitiated"],
};

interface OnboardingProgressBarProps {
	progress: OrganizationOnboardingProgressDto;
	defaultCollapsed?: boolean;
	steps?: readonly Milestone[];
	readOnly?: boolean;
}

export function OnboardingProgressBar({ progress, defaultCollapsed = false, steps, readOnly = false }: OnboardingProgressBarProps) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [collapsed, setCollapsed] = useState(defaultCollapsed);

	const milestones = steps ?? allMilestones;
	const stepNames = new Set<string>(progress.steps.map((s) => s.stepName));

	const completedSteps = new Set<string>();
	const pendingSteps = new Set<string>();

	for (const milestone of milestones) {
		if (stepNames.has(milestone.key)) {
			completedSteps.add(milestone.key);
		} else {
			const pendingKeys = pendingMappings[milestone.key];
			if (pendingKeys?.some((k) => stepNames.has(k))) {
				pendingSteps.add(milestone.key);
			}
		}
	}

	const completedCount = milestones.filter((m) => completedSteps.has(m.key)).length;
	const pendingCount = milestones.filter((m) => pendingSteps.has(m.key)).length;
	const progressCount = completedCount + pendingCount;
	const allComplete = completedCount === milestones.length;

	const updateState = (state: string) => axios.patch("/api/users/me/settings", { onboardingProgressState: state });

	const dismissMutation = useMutation({
		mutationFn: () => updateState("Dismissed"),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
		},
	});

	const toggleCollapsed = () => {
		const next = !collapsed;
		setCollapsed(next);
		updateState(next ? "Collapsed" : "Expanded");
	};

	const sorted = [...milestones].sort((a, b) => {
		const stateOrder = (key: string) => completedSteps.has(key) ? 2 : pendingSteps.has(key) ? 1 : 0;
		return stateOrder(a.key) - stateOrder(b.key);
	});
	const firstNotCompleted = milestones.find((m) => !completedSteps.has(m.key));
	const firstActionable = firstNotCompleted && !pendingSteps.has(firstNotCompleted.key) ? firstNotCompleted : undefined;

	const handleCtaClick = (milestone: Milestone) => {
		if (milestone.onClick) {
			milestone.onClick();
		} else if (milestone.route) {
			navigate({ to: milestone.route });
		}
	};

	return (
		<div className="bg-white border border-gray-200 rounded-lg p-5">
			<div className="flex items-center justify-between mb-4">
				<div>
					<h3 className="text-lg font-semibold text-gray-900">Get Started</h3>
					<p className="text-sm text-gray-500">{progressCount} of {milestones.length} complete</p>
				</div>
				{!readOnly && (
					allComplete ? (
						<button type="button" className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => dismissMutation.mutate()} disabled={dismissMutation.isPending}>
							Dismiss
						</button>
					) : (
						<button type="button" className="text-gray-400 hover:text-gray-600 cursor-pointer" onClick={toggleCollapsed}>
							{collapsed ? <ChevronDown className="size-5" /> : <ChevronUp className="size-5" />}
						</button>
					)
				)}
			</div>

			<div className="w-full bg-gray-100 rounded-full h-2 mb-5 flex overflow-hidden">
				{completedCount > 0 && (
					<div className="bg-brand-600 h-2 transition-all duration-500" style={{ width: `${(completedCount / milestones.length) * 100}%` }} />
				)}
				{pendingCount > 0 && (
					<div className="h-2 transition-all duration-500" style={{ width: `${(pendingCount / milestones.length) * 100}%`, backgroundImage: "repeating-linear-gradient(-45deg, #f59e0b, #f59e0b 3px, #fbbf24 3px, #fbbf24 6px)", backgroundSize: "8.5px 100%", animation: "progress-stripes 0.5s linear infinite" }} />
				)}
			</div>

			{!readOnly && collapsed ? (
				firstNotCompleted && (
					<div className="flex gap-3">
						<div className="flex flex-col items-center">
							{pendingSteps.has(firstNotCompleted.key) ? (
								<Clock className="size-7 text-warning-500 flex-shrink-0" />
							) : (
								<div className="size-7 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
									<span className="text-xs font-semibold text-white">1</span>
								</div>
							)}
						</div>
						<div className="flex-1 min-w-0">
							<div className="flex items-center justify-between gap-2">
								<span className={pendingSteps.has(firstNotCompleted.key) ? "text-sm text-warning-700 font-semibold" : "text-sm text-gray-900 font-semibold"}>{firstNotCompleted.label}</span>
								{firstActionable && (
									<Button color="primary" size="sm" className="!py-1 !px-2.5 !text-xs flex-shrink-0" isLoading={firstActionable.isLoading} onClick={() => handleCtaClick(firstActionable)}>{firstActionable.verb}</Button>
								)}
							</div>
							{pendingSteps.has(firstNotCompleted.key) ? (
								<p className="text-xs text-warning-500 mt-0.5">In progress</p>
							) : (
								<p className="text-xs text-gray-500 mt-0.5">{firstNotCompleted.description}</p>
							)}
						</div>
					</div>
				)
			) : (
				<div className="relative">
					{sorted.map((milestone, index) => {
						const isComplete = completedSteps.has(milestone.key);
						const isPending = pendingSteps.has(milestone.key);
						const isLast = index === sorted.length - 1;
						const incompleteIndex = sorted.filter((m, i) => i <= index && !completedSteps.has(m.key) && !pendingSteps.has(m.key)).length;
						return (
							<div key={milestone.key} className="flex gap-3">
								<div className="flex flex-col items-center">
									{isComplete ? (
										<CheckCircle className="size-7 text-brand-600 flex-shrink-0" />
									) : isPending ? (
										<Clock className="size-7 text-warning-500 flex-shrink-0" />
									) : (
										<div className="size-7 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
											<span className="text-xs font-semibold text-white">{incompleteIndex}</span>
										</div>
									)}
									{!isLast && (
										<div className={`w-px flex-1 min-h-4 my-1.5 ${isComplete ? "bg-brand-600" : isPending ? "bg-warning-500" : "bg-gray-200"}`} />
									)}
								</div>
								<div className={`pb-4 ${isLast ? "pb-0" : ""} flex-1 min-w-0`}>
									<div className="flex items-center justify-between gap-2">
										<span className={isComplete ? "text-sm text-gray-400 line-through" : isPending ? "text-sm text-warning-700 font-semibold" : "text-sm text-gray-900 font-semibold"}>{milestone.label}</span>
										{milestone.key === firstActionable?.key && (
											<Button color="primary" size="sm" className="!py-1 !px-2.5 !text-xs flex-shrink-0" isLoading={milestone.isLoading} onClick={() => handleCtaClick(milestone)}>{milestone.verb}</Button>
										)}
									</div>
									{!isComplete && !isPending && (
										<p className="text-xs text-gray-500 mt-0.5">{milestone.description}</p>
									)}
									{isPending && (
										<p className="text-xs text-warning-500 mt-0.5">In progress</p>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}